import { useEffect } from 'react'

const SITE_URL = 'https://itsgleam.com'
const DEFAULT_TITLE = 'GLEAM — AI companion и roleplay chat 18+'
const DEFAULT_DESCRIPTION =
  'GLEAM — 18+ AI companion и roleplay chat: выбирайте виртуальную модель, начинайте общение без регистрации и продолжайте с полным доступом после бесплатной регистрации.'
const DEFAULT_IMAGE = `${SITE_URL}/models/realism/reg.jpg`

interface PageSeoConfig {
  title?: string
  description?: string
  canonicalPath?: string
  robots?: string
  image?: string
  locale?: string
  alternates?: { hrefLang: string; href: string }[]
  structuredData?: Record<string, unknown> | Record<string, unknown>[]
}

export function usePageSeo(config: PageSeoConfig) {
  useEffect(() => {
    const title = config.title ?? DEFAULT_TITLE
    const description = config.description ?? DEFAULT_DESCRIPTION
    const canonicalUrl = buildCanonicalUrl(config.canonicalPath ?? '/')
    const robots = config.robots ?? 'index, follow, max-image-preview:large'
    const image = config.image ?? DEFAULT_IMAGE
    const locale = config.locale ?? 'ru_RU'

    document.title = title
    document.documentElement.lang = locale.startsWith('ru') ? 'ru' : 'en'

    setMeta('name', 'description', description)
    setMeta('name', 'robots', robots)
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:site_name', 'GLEAM')
    setMeta('property', 'og:locale', locale)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonicalUrl)
    setMeta('property', 'og:image', absoluteUrl(image))
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image', absoluteUrl(image))
    setCanonical(canonicalUrl)
    setAlternates(config.alternates ?? [])
    setStructuredData(config.structuredData)
  }, [
    config.alternates,
    config.canonicalPath,
    config.description,
    config.image,
    config.locale,
    config.robots,
    config.structuredData,
    config.title,
  ])
}

export function buildCanonicalUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalizedPath}`
}

function absoluteUrl(value: string) {
  if (/^https?:\/\//i.test(value)) return value
  return buildCanonicalUrl(value)
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }

  element.content = content
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')

  if (!element) {
    element = document.createElement('link')
    element.rel = 'canonical'
    document.head.appendChild(element)
  }

  element.href = href
}

function setAlternates(alternates: { hrefLang: string; href: string }[]) {
  document.head.querySelectorAll('link[data-gleam-seo="alternate"]').forEach((element) => element.remove())

  alternates.forEach((alternate) => {
    const element = document.createElement('link')
    element.rel = 'alternate'
    element.hreflang = alternate.hrefLang
    element.href = buildCanonicalUrl(alternate.href)
    element.dataset.gleamSeo = 'alternate'
    document.head.appendChild(element)
  })
}

function setStructuredData(data: PageSeoConfig['structuredData']) {
  document.head.querySelectorAll('script[data-gleam-seo="structured-data"]').forEach((element) => element.remove())

  if (!data) return

  const items = Array.isArray(data) ? data : [data]
  items.forEach((item) => {
    const element = document.createElement('script')
    element.type = 'application/ld+json'
    element.dataset.gleamSeo = 'structured-data'
    element.text = JSON.stringify(item)
    document.head.appendChild(element)
  })
}
